import React, { useEffect, useState } from "react";
import { LogOut, CheckCircle2, XCircle, Clock, Plus, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

import { AdminProductsTab } from "./admin/AdminProductsTab";
import { AdminEventsTab } from "./admin/AdminEventsTab";
import { AdminBlogTab } from "./admin/AdminBlogTab";
import { AdminLookbookTab } from "./admin/AdminLookbookTab";
import { AdminSiteTab } from "./admin/AdminSiteTab";
import { AdminRosterTab } from "./admin/AdminRosterTab";
import { AdminLedgerTab } from "./admin/AdminLedgerTab";
import { AdminDiscountsTab } from "./admin/AdminDiscountsTab";
import { AdminSalesTab } from "./admin/AdminSalesTab";
import { AdminAbandonedTab } from "./admin/AdminAbandonedTab";
import { AdminCollectionsTab } from "./admin/AdminCollectionsTab";
import { AdminNavTab } from "./admin/AdminNavTab";
import { AdminTagsTab } from "./admin/AdminTagsTab";
import { AdminEventTypesTab } from "./admin/AdminEventTypesTab";
import { AdminMessagesTab } from "./admin/AdminMessagesTab";
import { AdminInsightsTab } from "./admin/AdminInsightsTab";
import { AdminAdminsTab } from "./admin/AdminAdminsTab";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [blog, setBlog] = useState([]);
  const [lookbook, setLookbook] = useState([]);
  const [artistsList, setArtistsList] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("insights");

  // create account
  const [showCreate, setShowCreate] = useState(false);
  const [accountForm, setAccountForm] = useState({
    email: "",
    name: "",
    password: "",
    kind: "wrestler",
    royalty_percent: 30,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // review request
  const [respondingTo, setRespondingTo] = useState(null);
  const [adminResponse, setAdminResponse] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, r, p, ev, bl, lb, ar, dc] = await Promise.all([
        api.get("/admin/artist-accounts").then((x) => x.data),
        api.get("/admin/requests").then((x) => x.data),
        api.get("/products").then((x) => x.data),
        api.get("/events").then((x) => x.data),
        api.get("/blog").then((x) => x.data),
        api.get("/lookbook").then((x) => x.data),
        api.get("/artists").then((x) => x.data),
        api.get("/admin/discounts").then((x) => x.data).catch(() => []),
      ]);
      setAccounts(a);
      setRequests(r);
      setProducts(p);
      setEvents(ev);
      setBlog(bl);
      setLookbook(lb);
      setArtistsList(ar);
      setDiscounts(dc);
    } catch {
      // ignore
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const createAccount = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/admin/artist-accounts", accountForm);
      setAccountForm({ email: "", name: "", password: "", kind: "wrestler", royalty_percent: 30 });
      setShowCreate(false);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  const updateRoyalty = async (userId, pct) => {
    try {
      await api.patch(`/admin/artist-accounts/${userId}/royalty`, { royalty_percent: Number(pct) });
      await refresh();
    } catch {
      // ignore
    }
  };

  const updateAccount = async (userId, patch) => {
    try {
      await api.patch(`/admin/artist-accounts/${userId}`, patch);
      await refresh();
      return true;
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to update");
      return false;
    }
  };

  const deleteAccount = async (userId, name) => {
    const purgeArtist = window.confirm(
      `Delete account for "${name}"?\n\nClick OK to ALSO delete the linked roster profile.\nClick Cancel to keep the roster profile (only the login is removed).`
    );
    const confirmFinal = window.confirm(
      `Final confirmation: this will remove the artist login${purgeArtist ? ", the roster profile" : ""}, and the artist's ledger entries. Proceed?`
    );
    if (!confirmFinal) return;
    try {
      await api.delete(`/admin/artist-accounts/${userId}`, {
        params: { purge_artist: purgeArtist, purge_ledger: true },
      });
      await refresh();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const reviewRequest = async (requestId, status) => {
    try {
      await api.patch(`/admin/requests/${requestId}`, {
        status,
        admin_response: adminResponse || null,
      });
      setRespondingTo(null);
      setAdminResponse("");
      await refresh();
    } catch {
      // ignore
    }
  };

  const totalGross = accounts.reduce((s, a) => s + (a.earnings?.gross_revenue || 0), 0);
  const totalPayout = accounts.reduce((s, a) => s + (a.earnings?.payout || 0), 0);
  const pendingCount = requests.filter((r) => r.status === "pending").length;
  const artistOptions = accounts.map((a) => a.artist?.name).filter(Boolean);

  const StatusIcon = ({ status }) => {
    if (status === "approved") return <CheckCircle2 size={16} className="text-kult-yellow" />;
    if (status === "rejected") return <XCircle size={16} className="text-red-400" />;
    return <Clock size={16} className="text-kult-muted" />;
  };

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12" data-testid="page-admin-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-10">
        <div>
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ ADMIN PORTAL</p>
          <h1 className="font-display text-5xl md:text-7xl mt-2 leading-none">
            ADMIN <span className="text-kult-pink">CONSOLE.</span>
          </h1>
          <p className="text-kult-muted mt-2 font-mono text-xs tracking-[0.25em]">{user?.email}</p>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 border border-kult-border px-4 py-2 hover:border-kult-pink font-mono text-xs tracking-[0.25em]"
          data-testid="admin-logout"
        >
          <LogOut size={14} /> LOG OUT
        </button>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Stat label="ARTISTS" value={accounts.length} testId="admin-stat-artists" />
        <Stat label="GROSS REV." value={`€${totalGross.toFixed(0)}`} testId="admin-stat-gross" />
        <Stat label="ARTIST PAYOUTS" value={`€${totalPayout.toFixed(0)}`} accent testId="admin-stat-payouts" />
        <Stat label="PENDING REQUESTS" value={pendingCount} testId="admin-stat-pending" />
      </section>

      <div className="flex gap-2 border-b border-kult-border mb-8 overflow-x-auto">
        <TabBtn active={tab === "insights"} onClick={() => setTab("insights")} testId="admin-tab-insights">INSIGHTS</TabBtn>
        <TabBtn active={tab === "sales"} onClick={() => setTab("sales")} testId="admin-tab-sales">SALES</TabBtn>
        <TabBtn active={tab === "abandoned"} onClick={() => setTab("abandoned")} testId="admin-tab-abandoned">CARTS</TabBtn>
        <TabBtn active={tab === "accounts"} onClick={() => setTab("accounts")} testId="admin-tab-accounts">ACCOUNTS</TabBtn>
        <TabBtn active={tab === "ledger"} onClick={() => setTab("ledger")} testId="admin-tab-ledger">LEDGER</TabBtn>
        <TabBtn active={tab === "discounts"} onClick={() => setTab("discounts")} testId="admin-tab-discounts">DISCOUNTS</TabBtn>
        <TabBtn active={tab === "roster"} onClick={() => setTab("roster")} testId="admin-tab-roster">ROSTER</TabBtn>
        <TabBtn active={tab === "products"} onClick={() => setTab("products")} testId="admin-tab-products">PRODUCTS</TabBtn>
        <TabBtn active={tab === "collections"} onClick={() => setTab("collections")} testId="admin-tab-collections">COLLECTIONS</TabBtn>
        <TabBtn active={tab === "tags"} onClick={() => setTab("tags")} testId="admin-tab-tags">TAGS</TabBtn>
        <TabBtn active={tab === "events"} onClick={() => setTab("events")} testId="admin-tab-events">EVENTS</TabBtn>
        <TabBtn active={tab === "event-types"} onClick={() => setTab("event-types")} testId="admin-tab-event-types">EVENT TYPES</TabBtn>
        <TabBtn active={tab === "blog"} onClick={() => setTab("blog")} testId="admin-tab-blog">JOURNAL</TabBtn>
        <TabBtn active={tab === "lookbook"} onClick={() => setTab("lookbook")} testId="admin-tab-lookbook">LOOKBOOK</TabBtn>
        <TabBtn active={tab === "site"} onClick={() => setTab("site")} testId="admin-tab-site">SITE COPY</TabBtn>
        <TabBtn active={tab === "nav"} onClick={() => setTab("nav")} testId="admin-tab-nav">NAV</TabBtn>
        <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testId="admin-tab-messages">MESSAGES</TabBtn>
        <TabBtn active={tab === "requests"} onClick={() => setTab("requests")} testId="admin-tab-requests">
          REQUESTS{pendingCount > 0 && <span className="ml-1 bg-kult-pink text-black px-1.5 text-[10px]">{pendingCount}</span>}
        </TabBtn>
      </div>

      {loading ? (
        <p className="font-mono text-xs text-kult-muted">LOADING...</p>
      ) : tab === "insights" ? (
        <AdminInsightsTab />
      ) : tab === "sales" ? (
        <AdminSalesTab />
      ) : tab === "abandoned" ? (
        <AdminAbandonedTab discounts={discounts} />
      ) : tab === "ledger" ? (
        <AdminLedgerTab artists={artistsList} refresh={refresh} />
      ) : tab === "discounts" ? (
        <AdminDiscountsTab products={products} />
      ) : tab === "accounts" ? (
        <AccountsTab
          accounts={accounts}
          accountForm={accountForm}
          setAccountForm={setAccountForm}
          showCreate={showCreate}
          setShowCreate={setShowCreate}
          createAccount={createAccount}
          updateRoyalty={updateRoyalty}
          updateAccount={updateAccount}
          deleteAccount={deleteAccount}
          submitting={submitting}
          error={error}
        />
      ) : tab === "products" ? (
        <AdminProductsTab products={products} artistOptions={artistOptions} refresh={refresh} />
      ) : tab === "collections" ? (
        <AdminCollectionsTab />
      ) : tab === "tags" ? (
        <AdminTagsTab />
      ) : tab === "roster" ? (
        <AdminRosterTab artists={artistsList} accounts={accounts} refresh={refresh} />
      ) : tab === "events" ? (
        <AdminEventsTab events={events} refresh={refresh} />
      ) : tab === "event-types" ? (
        <AdminEventTypesTab />
      ) : tab === "blog" ? (
        <AdminBlogTab posts={blog} refresh={refresh} />
      ) : tab === "lookbook" ? (
        <AdminLookbookTab items={lookbook} refresh={refresh} />
      ) : tab === "site" ? (
        <AdminSiteTab />
      ) : tab === "nav" ? (
        <AdminNavTab />
      ) : tab === "messages" ? (
        <AdminMessagesTab />
      ) : (
        <RequestsTab
          requests={requests}
          respondingTo={respondingTo}
          setRespondingTo={setRespondingTo}
          adminResponse={adminResponse}
          setAdminResponse={setAdminResponse}
          reviewRequest={reviewRequest}
          StatusIcon={StatusIcon}
        />
      )}
    </div>
  );
}

const AccountsTab = ({ accounts, accountForm, setAccountForm, showCreate, setShowCreate, createAccount, updateRoyalty, updateAccount, deleteAccount, submitting, error }) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ email: "", name: "", password: "", royalty_percent: 30 });
  const [savingEdit, setSavingEdit] = useState(false);

  const startEdit = (acc) => {
    setEditingId(acc.user.id);
    setEditForm({
      email: acc.user.email,
      name: acc.artist?.name || acc.user.name || "",
      password: "",
      royalty_percent: acc.user.royalty_percent || 30,
    });
  };

  const saveEdit = async (userId) => {
    setSavingEdit(true);
    const patch = {};
    if (editForm.email) patch.email = editForm.email;
    if (editForm.name) patch.name = editForm.name;
    if (editForm.password) patch.password = editForm.password;
    if (editForm.royalty_percent !== undefined) patch.royalty_percent = Number(editForm.royalty_percent);
    const ok = await updateAccount(userId, patch);
    setSavingEdit(false);
    if (ok) setEditingId(null);
  };

  return (
  <section data-testid="admin-accounts-section">
    <AdminAdminsTab />
    <div className="border-t border-kult-border my-8" />
    <div className="flex items-center justify-between mb-4">
      <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ ARTIST ACCOUNTS</p>
      <button
        onClick={() => setShowCreate(!showCreate)}
        className="bg-kult-pink text-black px-5 py-2 font-display text-lg hover:bg-kult-yellow inline-flex items-center gap-1"
        data-testid="admin-new-account-toggle"
      >
        {showCreate ? <X size={16} /> : <Plus size={16} />} {showCreate ? "CLOSE" : "NEW ACCOUNT"}
      </button>
    </div>
    {showCreate && (
      <form onSubmit={createAccount} className="border-2 border-kult-pink p-6 bg-kult-black2 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-new-account-form">
        <Field label="EMAIL" type="email" required value={accountForm.email} onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })} testId="new-acc-email" />
        <Field label="NAME / STAGE NAME" required value={accountForm.name} onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })} testId="new-acc-name" />
        <Field label="PASSWORD" type="password" required value={accountForm.password} onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })} testId="new-acc-password" />
        <Select label="ROLE" value={accountForm.kind} onChange={(e) => setAccountForm({ ...accountForm, kind: e.target.value })}
          options={[{ v: "wrestler", l: "WRESTLER" }, { v: "artist", l: "ARTIST / MUSICIAN" }]} testId="new-acc-kind" />
        <div className="flex items-end">
          <button type="submit" disabled={submitting} className="w-full bg-kult-pink text-black font-display text-xl py-3 hover:bg-kult-yellow disabled:opacity-50" data-testid="new-acc-submit">
            {submitting ? "..." : "CREATE ACCOUNT"}
          </button>
        </div>
        <p className="md:col-span-2 font-mono text-[10px] tracking-[0.25em] text-kult-muted">
          NOTE: Royalty % is now set <strong>per product</strong> in the PRODUCTS tab — not per account.
        </p>
        {error && <p className="md:col-span-2 font-mono text-xs text-kult-pink">{error}</p>}
      </form>
    )}
    <div className="space-y-3">
      {accounts.map((acc) => (
        <article key={acc.user.id} className="border border-kult-border p-5 hover:border-kult-pink" data-testid={`admin-account-${acc.user.id}`}>
          {editingId === acc.user.id ? (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid={`account-edit-form-${acc.user.id}`}>
              <Field className="md:col-span-3" label="NAME" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} testId={`account-edit-name-${acc.user.id}`} />
              <Field className="md:col-span-3" label="EMAIL" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} testId={`account-edit-email-${acc.user.id}`} />
              <Field className="md:col-span-4" label="NEW PASSWORD (blank = keep)" type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} testId={`account-edit-password-${acc.user.id}`} />
              <div className="md:col-span-2 flex gap-2">
                <button onClick={() => saveEdit(acc.user.id)} disabled={savingEdit} className="flex-1 bg-kult-yellow text-black px-3 py-3 font-mono text-xs tracking-[0.25em] disabled:opacity-50" data-testid={`account-edit-save-${acc.user.id}`}>{savingEdit ? "..." : "SAVE"}</button>
                <button onClick={() => setEditingId(null)} className="flex-1 border border-kult-border px-3 py-3 font-mono text-xs tracking-[0.25em]" data-testid={`account-edit-cancel-${acc.user.id}`}>CANCEL</button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
              <div className="md:col-span-4 flex items-center gap-3">
                {acc.artist?.image && <img src={acc.artist.image} alt="" className="w-14 h-14 object-cover grayscale border border-kult-border" />}
                <div>
                  <h3 className="font-display text-2xl leading-none">{acc.artist?.name}</h3>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-kult-muted mt-1">{acc.artist?.role}</p>
                  <p className="font-mono text-[11px] text-kult-muted mt-1">{acc.user.email}</p>
                </div>
              </div>
              <div className="md:col-span-2"><Stat2 label="GROSS" value={`€${acc.earnings.gross_revenue.toFixed(0)}`} /></div>
              <div className="md:col-span-2"><Stat2 label="PAYOUT" value={`€${acc.earnings.payout.toFixed(0)}`} accent /></div>
              <div className="md:col-span-2"><Stat2 label="UNITS" value={acc.earnings.units_sold} /></div>
              <div className="md:col-span-2 flex gap-2 justify-end">
                <button onClick={() => startEdit(acc)} className="border border-kult-border px-3 py-2 font-mono text-[10px] tracking-[0.25em] hover:border-kult-pink hover:text-kult-pink" data-testid={`account-edit-btn-${acc.user.id}`}>EDIT</button>
                <button onClick={() => deleteAccount(acc.user.id, acc.artist?.name || acc.user.email)} className="border border-red-500 text-red-400 px-3 py-2 font-mono text-[10px] tracking-[0.25em] hover:bg-red-500/10" data-testid={`account-delete-${acc.user.id}`}>DELETE</button>
              </div>
            </div>
          )}
        </article>
      ))}
    </div>
  </section>
  );
};

const RequestsTab = ({ requests, respondingTo, setRespondingTo, adminResponse, setAdminResponse, reviewRequest, StatusIcon }) => (
  <section data-testid="admin-requests-section">
    <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-4">/ REQUESTS</p>
    {requests.length === 0 ? (
      <p className="font-mono text-sm text-kult-muted py-6">No requests submitted yet.</p>
    ) : (
      <div className="space-y-3">
        {requests.map((r) => (
          <article key={r.id} className="border border-kult-border p-5 hover:border-kult-pink" data-testid={`admin-request-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className="font-mono text-[10px] tracking-[0.3em] bg-kult-yellow text-black px-2 py-0.5">{r.type === "merch" ? "MERCH" : "EVENT"}</span>
                  <span className="font-mono text-[10px] tracking-[0.25em] text-kult-pink">{r.artist_name}</span>
                  <span className="font-mono text-[10px] tracking-[0.25em] text-kult-muted">{new Date(r.created_at).toLocaleDateString()}</span>
                  <span className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.25em] uppercase"><StatusIcon status={r.status} /> {r.status}</span>
                </div>
                <h3 className="font-display text-2xl">{r.title}</h3>
                <p className="text-kult-muted text-sm mt-1">{r.description}</p>
                {r.target_date && <p className="font-mono text-[11px] text-kult-muted mt-1">TARGET · {r.target_date}</p>}
                {r.reference_image_url && <a href={r.reference_image_url} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-kult-yellow kult-link inline-block mt-1">REFERENCE IMAGE →</a>}
                {r.notes && <p className="text-kult-muted text-sm mt-2"><span className="font-mono text-[10px] text-kult-yellow">NOTES · </span>{r.notes}</p>}
                {r.admin_response && <p className="mt-3 border-l-2 border-kult-pink pl-3 text-sm"><span className="font-mono text-[10px] tracking-[0.3em] text-kult-pink">YOU SAID · </span>{r.admin_response}</p>}
              </div>
            </div>
            {r.status === "pending" && (
              <div className="mt-4 border-t border-kult-border pt-4">
                {respondingTo === r.id ? (
                  <div className="space-y-3">
                    <textarea rows={2} placeholder="Reply (optional)" value={adminResponse} onChange={(e) => setAdminResponse(e.target.value)} className="w-full bg-transparent border border-kult-border px-3 py-2 font-mono text-sm focus:border-kult-pink outline-none" data-testid={`admin-response-${r.id}`} />
                    <div className="flex gap-2">
                      <button onClick={() => reviewRequest(r.id, "approved")} className="bg-kult-yellow text-black px-4 py-2 font-mono text-xs tracking-[0.25em]" data-testid={`approve-${r.id}`}>APPROVE</button>
                      <button onClick={() => reviewRequest(r.id, "rejected")} className="bg-red-500 text-black px-4 py-2 font-mono text-xs tracking-[0.25em]" data-testid={`reject-${r.id}`}>REJECT</button>
                      <button onClick={() => { setRespondingTo(null); setAdminResponse(""); }} className="border border-kult-border px-4 py-2 font-mono text-xs tracking-[0.25em]">CANCEL</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setRespondingTo(r.id)} className="bg-kult-pink text-black px-4 py-2 font-mono text-xs tracking-[0.25em]" data-testid={`review-${r.id}`}>REVIEW</button>
                )}
              </div>
            )}
          </article>
        ))}
      </div>
    )}
  </section>
);

function Stat({ label, value, accent, testId }) {
  return (
    <div className={`border-2 ${accent ? "border-kult-pink bg-kult-black2" : "border-kult-border"} p-5`} data-testid={testId}>
      <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted">{label}</p>
      <p className={`font-display text-4xl mt-2 ${accent ? "text-kult-pink" : "text-white"}`}>{value}</p>
    </div>
  );
}
function Stat2({ label, value, accent }) {
  return (
    <>
      <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted">{label}</p>
      <p className={`font-display text-2xl ${accent ? "text-kult-pink" : "text-white"}`}>{value}</p>
    </>
  );
}
function TabBtn({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`font-mono text-xs tracking-[0.25em] px-4 py-3 border-b-2 -mb-px whitespace-nowrap transition-colors ${active ? "border-kult-pink text-kult-pink" : "border-transparent text-white hover:text-kult-pink"}`}>
      {children}
    </button>
  );
}
function Field({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <input {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
function Select({ label, options, testId, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <select {...props} data-testid={testId} className="w-full bg-kult-black border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
